import { useState } from "react";
import { Sparkles, X, LoaderCircle, Keyboard, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import type { Icd10Code } from "@/lib/queries";
import { useAiIcd10 } from "@/lib/mutations";
import { toast } from "sonner";
import { Has } from "@/components/Has";
import { ManualIcd10SearchDialog } from "./ManualIcd10SearchDialog";

interface Props {
  codes: Icd10Code[];
  onChange: (codes: Icd10Code[]) => void;
  diagnosis: string;
  patientContext?: { age?: number; sex?: string; chronicConditions?: string[] };
}

/**
 * ICD-10 codes for the EMR visit page (rework 2026-11-03).
 *
 * The card renders ONE button — "Generate ICD10" — which opens the main
 * dialog (Icd10Dialog). The codes themselves are NOT listed on the card
 * (they occupied too much visit-page space — user request 2026-11-03); a
 * green tick before the button label marks that the visit has codes:
 *   - LEFT box: the visit's current diagnosis, read-only (context for the
 *     generation; editing stays in the Visit details column).
 *   - RIGHT box: the visit's ICD-10 codes, non-textual list — each row shows
 *     code + description with a single [X] remove button. No inline text
 *     editing (the old free-text Code/Description inputs were removed with
 *     the old manual dialog).
 *   - "Generate ICD 10 code" button → POST /ai/icd10 with the visit's
 *     diagnosis + patient context; returned codes MERGE into the box
 *     (dedupe by code), never replacing what is already there.
 *   - "Manual entry" button → ManualIcd10SearchDialog (searchable AI-backed
 *     catalog picker); a picked code merges into the box the same way.
 *
 * Every change (generate / manual add / [X] remove) commits IMMEDIATELY via
 * onChange → the visit page's 1s-debounced notes autosave (user decision
 * 2026-11-03: no draft-then-Save inside the dialog).
 */
export function Icd10Box({ codes, onChange, diagnosis, patientContext }: Props) {
  const [open, setOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  const remove = (idx: number) => {
    onChange(codes.filter((_, i) => i !== idx));
  };

  // Merge new codes into the visit's list — dedupe by code, keep existing
  // order, toast the added count. Returns false (and does NOT call onChange)
  // when every candidate is already present.
  const mergeCodes = (incoming: Icd10Code[]): boolean => {
    const merged = [...codes];
    let added = 0;
    for (const c of incoming) {
      if (!merged.some((m) => m.code === c.code)) {
        merged.push(c);
        added++;
      }
    }
    if (added === 0) return false;
    onChange(merged);
    toast.success(`Added ${added} ICD-10 code${added === 1 ? "" : "s"}`);
    return true;
  };

  return (
    <div className="shrink-0 rounded-lg border border-cream-200 bg-cream-50/50 p-3 dark:border-dark-700 dark:bg-dark-800/50">
      {/* Single row: label + button (+ green tick when codes exist). The codes
          themselves are NOT shown on the card — they occupy too much visit-page
          space (user request 2026-11-03); open the dialog to see/manage them. */}
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted">ICD-10 Codes</Label>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOpen(true)}
          title={
            codes.length > 0
              ? `Open the ICD-10 dialog — ${codes.length} code${codes.length === 1 ? "" : "s"} on this visit`
              : "Open the ICD-10 dialog — generate codes from the diagnosis or add them manually"
          }
        >
          {/* Green tick before the label when the visit already has codes. */}
          {codes.length > 0 && (
            <span className="mr-1 inline-flex text-green-600 dark:text-green-400" title={`${codes.length} ICD-10 code${codes.length === 1 ? "" : "s"} recorded`}>
              <CircleCheck size={14} />
            </span>
          )}
          <Sparkles size={14} className="mr-1" /> Generate ICD10
        </Button>
      </div>

      <Icd10Dialog
        open={open}
        onOpenChange={setOpen}
        diagnosis={diagnosis}
        codes={codes}
        onRemove={remove}
        onMerge={mergeCodes}
        patientContext={patientContext}
        onManualEntry={() => setManualOpen(true)}
      />

      {/* Manual-entry picker rendered OUTSIDE the main dialog's tree so it
          stays independently controllable while the main dialog is open. */}
      <ManualIcd10SearchDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        onPick={(c) => {
          if (!mergeCodes([c])) toast.info(`ICD-10 ${c.code} already added`);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main side-by-side dialog
// ---------------------------------------------------------------------------

interface Icd10DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  diagnosis: string;
  codes: Icd10Code[];
  onRemove: (idx: number) => void;
  onMerge: (incoming: Icd10Code[]) => boolean;
  patientContext?: { age?: number; sex?: string; chronicConditions?: string[] };
  onManualEntry: () => void;
}

function Icd10Dialog({
  open, onOpenChange, diagnosis, codes, onRemove, onMerge, patientContext, onManualEntry,
}: Icd10DialogProps) {
  const aiMut = useAiIcd10();

  const generate = async () => {
    if (!diagnosis.trim()) {
      toast.error("No diagnosis on the visit yet — enter one in the Visit details column first.");
      return;
    }
    try {
      const res = await aiMut.mutateAsync({ diagnosis, patientContext });
      if (res.codes.length === 0) {
        toast.info(res.message ?? "No codes suggested — use the Manual entry button to add one.");
      } else if (!onMerge(res.codes)) {
        toast.info(`All ${res.codes.length} suggested code${res.codes.length === 1 ? "" : "s"} already added`);
      }
    } catch {
      // toast handled by mutation onError
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-maroon-700 dark:text-amber-500">ICD-10 Codes</DialogTitle>
          <DialogDescription>
            Generate codes from the visit&rsquo;s diagnosis, or add them manually. Closes are saved automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-1 md:grid-cols-2">
          {/* LEFT — the visit's current diagnosis (read-only context) */}
          <div className="flex min-h-40 flex-col gap-1">
            <Label className="shrink-0 text-xs font-semibold uppercase tracking-wide text-muted">
              Diagnosis <span className="normal-case font-normal">(from the visit page)</span>
            </Label>
            <div className="visible-scrollbar min-h-32 flex-1 overflow-y-auto whitespace-pre-wrap rounded-md border border-cream-200 bg-cream-100 px-3 py-2 text-sm text-ink dark:border-dark-700 dark:bg-dark-900 dark:text-cream-50">
              {diagnosis.trim() ? diagnosis : <span className="text-muted">No diagnosis entered yet.</span>}
            </div>
          </div>

          {/* RIGHT — the visit's ICD-10 codes (non-textual; one [X] per row) */}
          <div className="flex min-h-40 flex-col gap-1">
            <Label className="shrink-0 text-xs font-semibold uppercase tracking-wide text-muted">
              ICD-10 Codes <span className="normal-case font-normal">(generated &amp; manual)</span>
            </Label>
            <div className="visible-scrollbar min-h-32 flex-1 overflow-y-auto rounded-md border border-cream-200 bg-cream-100 dark:border-dark-700 dark:bg-dark-900">
              {codes.length === 0 ? (
                <div className="px-3 py-8 text-center text-sm text-muted">
                  No codes yet — click &ldquo;Generate ICD 10 code&rdquo; or &ldquo;Manual entry&rdquo;.
                </div>
              ) : (
                <ul className="space-y-1 p-1">
                  {codes.map((c, idx) => (
                    <li key={`${c.code}-${idx}`} className="flex items-center gap-2 rounded-md bg-white px-2 py-1 text-sm dark:bg-dark-800">
                      <span className="font-mono text-xs font-bold text-maroon-700 dark:text-amber-500">{c.code}</span>
                      <span className="min-w-0 flex-1 text-ink dark:text-cream-50">{c.description}</span>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-5 w-5 shrink-0 p-0 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                        title={`Remove ${c.code}`}
                        onClick={() => onRemove(idx)}
                      >
                        <X size={12} />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          {/* Manual search is AI-backed (POST /ai/icd10-search, ai.use-gated
              server-side) — gate the button to match, same as Generate. */}
          <Has perm="ai.use">
            <Button variant="outline" onClick={onManualEntry}>
              <Keyboard size={14} className="mr-1" /> Manual entry
            </Button>
            <Button
              className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"
              onClick={generate}
              disabled={aiMut.isPending || !diagnosis.trim()}
              title={!diagnosis.trim() ? "Enter a diagnosis in the Visit details column first" : undefined}
            >
              {aiMut.isPending ? <LoaderCircle size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />}
              {aiMut.isPending ? "Generating…" : "Generate ICD 10 code"}
            </Button>
          </Has>
        </div>
      </DialogContent>
    </Dialog>
  );
}